import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/ui/style-helpers'

export interface InputProps
  extends
    React.InputHTMLAttributes<HTMLInputElement>,
    Omit<VariantProps<typeof inputVariants>, 'disabled' | 'size'> {
  iconLeft?: React.ReactNode
  state?: 'default' | 'error'
  inputSize?: 'default' | 'large'
  suffix?: React.ReactNode
  suffixPosition?: 'end' | 'inline'
  prefix?: string
  addonBefore?: React.ReactNode
}

const inputVariants = cva(
  'flex w-full rounded border bg-main-background-card text-body-default text-main-foreground-primary px-2 py-1.5 outline-none ring-offset-main-background-card transition-colors placeholder:text-main-foreground-placeholder',
  {
    variants: {
      state: {
        default:
          'border-main-border-input hover:border-main-border-bold focus:border-main-border-focus focus:ring-offset focus:ring-main-border-focus',
        error:
          'border-main-border-error focus:border-main-border-error focus:ring-offset focus:ring-main-border-error',
      },
      size: {
        default: 'h-8',
        large: 'h-10',
      },
      disabled: {
        true: 'border-none bg-main-background-interactive-secondary-disabled text-main-foreground-disabled cursor-not-allowed',
        false: '',
      },
    },
    defaultVariants: {
      state: 'default',
      size: 'default',
      disabled: false,
    },
  },
)

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      iconLeft,
      state = 'default',
      inputSize = 'default',
      disabled,
      type = 'text',
      suffix,
      suffixPosition = 'end',
      prefix,
      ...props
    },
    ref,
  ) => {
    const hasError = state === 'error'
    const isInlineSuffix = !!suffix && suffixPosition === 'inline'
    const currentValue = typeof props.value === 'string' ? props.value : ''

    // Shared input element
    const inputElement = (
      <input
        ref={ref}
        type={type}
        aria-invalid={hasError}
        disabled={disabled}
        className={cn(
          prefix
            ? 'w-full bg-transparent px-2 py-1.5 text-main-foreground-primary outline-none text-body-default placeholder:text-main-foreground-placeholder'
            : inputVariants({
                state: hasError ? 'error' : 'default',
                size: inputSize,
                disabled: !!disabled,
              }),
          inputSize === 'large' ? 'h-10' : 'h-8',
          iconLeft ? 'pl-8' : '',
          suffix && suffixPosition === 'end' ? 'pr-20' : '',
          className,
        )}
        {...props}
      />
    )

    const iconLeftElement = iconLeft && (
      <span
        className={cn(
          'absolute left-3 top-1/2 flex -translate-y-1/2 items-center',
          prefix && 'z-10',
        )}
      >
        {iconLeft}
      </span>
    )

    const endSuffixElement = suffix && !isInlineSuffix && (
      <div className='pointer-events-none absolute right-2 top-1/2 flex -translate-y-1/2 items-center'>
        <div className='pointer-events-auto'>{suffix}</div>
      </div>
    )

    // Invisible text mirror + suffix in one flex row: the mirror takes the typed
    // text's width and naturally places the suffix after the last character.
    const inlineSuffixOverlay = isInlineSuffix && (
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-y-0 left-0 flex items-center overflow-hidden text-body-default',
          iconLeft ? 'pl-8' : 'pl-2',
        )}
      >
        <span className='invisible whitespace-pre'>{currentValue}</span>
        <span className='ml-2 flex shrink-0 items-center'>{suffix}</span>
      </div>
    )

    // Prefix layout
    if (prefix) {
      return (
        <div
          className={cn(
            'flex w-full items-center overflow-hidden rounded border bg-main-background-card',
            inputVariants({
              state: hasError ? 'error' : 'default',
              size: inputSize,
              disabled: !!disabled,
            })
              .split(' ')
              .filter(
                (cls) => cls.includes('border') || cls.includes('ring') || cls.includes('focus'),
              )
              .join(' '),
            { 'opacity-50': disabled },
          )}
        >
          <div className='flex items-center border-r border-main-border-default px-3 text-main-foreground-secondary text-body-default'>
            {prefix}
          </div>
          <div className='relative flex-1'>
            {iconLeftElement}
            {inlineSuffixOverlay}
            {inputElement}
            {endSuffixElement}
          </div>
        </div>
      )
    }

    // Default layout
    return (
      <div className={cn('relative flex w-full items-center', { 'opacity-50': disabled })}>
        {iconLeftElement}
        {inlineSuffixOverlay}
        {inputElement}
        {endSuffixElement}
      </div>
    )
  },
)
Input.displayName = 'Input'

export { Input }
